import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth, useRequiredProfile } from '../lib/auth';
import { useOptionalWorkspace } from '../lib/workspace';
import { queryKeys } from '../lib/query-keys';
import { BulkNaDialog } from '../components/register/StatusChangeDialog';
import {
  getAssessment,
  listRegisterAssignees,
  updateItemStatus,
} from '../lib/api';
import type {
  CustomerRegisterItemStatus,
  GapAnalysisRow,
  RiskCategory,
  Severity,
  SourceType,
} from '@aigovops/core';
import {
  Alert,
  Button,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  SeverityBadge,
  Spinner,
  StatusBadge,
} from '@aigovops/design-system';
import { categoryLabel } from '../lib/format';

type SortKey = 'priority' | 'severity' | 'title' | 'category' | 'status';

const SEVERITY_ORDINAL: Record<Severity, number> = {
  critical: 1,
  high: 2,
  elevated: 3,
  moderate: 4,
  standard: 5,
};

const SEVERITY_VALUES: Severity[] = ['critical', 'high', 'elevated', 'moderate', 'standard'];
const SOURCE_VALUES: SourceType[] = [
  'framework',
  'legislation',
  'case_law',
  'breach_intelligence',
  'best_practice',
  'expert_analysis',
];
const RISK_CATEGORY_VALUES: RiskCategory[] = [
  'governance',
  'access_control',
  'audit_logging',
  'incident_response',
  'change_management',
  'data_governance',
  'privacy',
  'security',
  'fairness_bias',
  'transparency',
  'safety',
  'supply_chain',
  'content_safety',
  'intellectual_property',
  'environmental',
];

function isSeverity(v: string | null): v is Severity {
  return v !== null && (SEVERITY_VALUES as string[]).includes(v);
}
function isRiskCategory(v: string | null): v is RiskCategory {
  return v !== null && (RISK_CATEGORY_VALUES as string[]).includes(v);
}
function isSourceType(v: string | null): v is SourceType {
  return v !== null && (SOURCE_VALUES as string[]).includes(v);
}

type FilterChipKey = 'all' | 'assigned_to_me' | 'unassigned' | 'blocked' | 'review_needed' | 'done';

const FILTER_CHIPS: { key: FilterChipKey; label: string }[] = [
  { key: 'all', label: 'All items' },
  { key: 'assigned_to_me', label: 'Assigned to me' },
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'blocked', label: 'Blocked' },
  { key: 'review_needed', label: 'Review needed' },
  { key: 'done', label: 'Closed' },
];

export function Register() {
  const profileId = useRequiredProfile();
  const { user } = useAuth();
  const navigate = useNavigate();
  const workspaceCtx = useOptionalWorkspace();
  const workspaceSlug = workspaceCtx?.workspace.slug ?? null;
  const prefix = workspaceCtx ? `/w/${workspaceCtx.workspace.slug}/app` : '/app'; // scope-check-ignore: legacy fallback
  const isAssessor = workspaceCtx?.role === 'assessor';
  const assessQuery = useQuery({
    queryKey: queryKeys.assessment(profileId),
    queryFn: () => getAssessment(profileId),
    enabled: !!profileId,
  });
  const assess = {
    data: assessQuery.data ?? null,
    error: assessQuery.error?.message ?? null,
    loading: assessQuery.isLoading,
    refetch: assessQuery.refetch,
  };
  const assigneesQuery = useQuery({
    queryKey: queryKeys.registerAssignees(workspaceSlug ?? ''),
    queryFn: () => listRegisterAssignees(workspaceSlug!),
    enabled: !!workspaceSlug,
  });
  const assignees = {
    data: assigneesQuery.data ?? null,
    error: assigneesQuery.error?.message ?? null,
    loading: assigneesQuery.isLoading,
    refetch: assigneesQuery.refetch,
  };
  const assigneeByItemId = useMemo(
    () => new Map(assignees.data?.assignees.map((a) => [a.register_item_id, a.assigned_to]) ?? []),
    [assignees.data]
  );
  const frameworkNameMap = useMemo(
    () => new Map(assess.data?.framework_scores.map((fs) => [fs.framework_id, fs.framework_name]) ?? []),
    [assess.data]
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const rawSeverity = searchParams.get('severity');
  const rawCategory = searchParams.get('category');
  const rawSource = searchParams.get('source');
  const search = searchParams.get('q') ?? '';
  const chipFilter: FilterChipKey = (searchParams.get('chip') as FilterChipKey) || 'all';
  const severityFilter: Severity | 'all' = isSeverity(rawSeverity) ? rawSeverity : 'all';
  const categoryFilter: RiskCategory | 'all' = isRiskCategory(rawCategory) ? rawCategory : 'all';
  const sourceFilter: SourceType | 'all' = isSourceType(rawSource) ? rawSource : 'all';

  const setParam = (key: string, value: string | null) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (value === null || value === '') params.delete(key);
        else params.set(key, value);
        return params;
      },
      { replace: true },
    );
  };
  const setSearch = (next: string) => setParam('q', next.length > 0 ? next : null);
  const setSeverityFilter = (next: Severity | 'all') => setParam('severity', next === 'all' ? null : next);
  const setCategoryFilter = (next: RiskCategory | 'all') => setParam('category', next === 'all' ? null : next);
  const setSourceFilter = (next: SourceType | 'all') => setParam('source', next === 'all' ? null : next);
  const setChipFilter = (next: FilterChipKey) => setParam('chip', next === 'all' ? null : next);

  const [sortKey, setSortKey] = useState<SortKey>('priority');
  const [sortAsc, setSortAsc] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showBulkNa, setShowBulkNa] = useState(false);
  const [undoAction, setUndoAction] = useState<{ ids: string[]; previousStatuses: Map<string, string>; timer: ReturnType<typeof setTimeout> } | null>(null);
  const undoRef = useRef(undoAction);
  undoRef.current = undoAction;

  const rows: GapAnalysisRow[] = assess.data?.gap_rows ?? [];

  // Glance-strip counts
  const glance = useMemo(() => {
    let open = 0, blocked = 0, reviewNeeded = 0, closed = 0;
    for (const r of rows) {
      if (r.status === 'done' || r.status === 'not_applicable') closed++;
      else if (r.status === 'blocked') blocked++;
      else if (r.status === 'review_needed') reviewNeeded++;
      else open++;
    }
    return { open, blocked, reviewNeeded, closed, total: rows.length };
  }, [rows]);

  // Filter chip counts
  const chipCounts = useMemo(() => {
    const c: Record<FilterChipKey, number> = {
      all: rows.length,
      assigned_to_me: 0,
      unassigned: 0,
      blocked: 0,
      review_needed: 0,
      done: 0,
    };
    for (const r of rows) {
      const assignee = assigneeByItemId.get(r.item_id);
      if (assignee?.user_id === user?.id) c.assigned_to_me++;
      if (!assignee) c.unassigned++;
      if (r.status === 'blocked') c.blocked++;
      if (r.status === 'review_needed') c.review_needed++;
      if (r.status === 'done' || r.status === 'not_applicable') c.done++;
    }
    return c;
  }, [rows, assigneeByItemId, user?.id]);

  const filtered = useMemo(() => {
    let list = rows;
    if (severityFilter !== 'all') list = list.filter((r) => r.severity === severityFilter);
    if (categoryFilter !== 'all') list = list.filter((r) => r.category === categoryFilter);
    if (sourceFilter !== 'all') list = list.filter((r) => r.source_type === sourceFilter);
    switch (chipFilter) {
      case 'assigned_to_me':
        list = list.filter((r) => assigneeByItemId.get(r.item_id)?.user_id === user?.id);
        break;
      case 'unassigned':
        list = list.filter((r) => !assigneeByItemId.get(r.item_id));
        break;
      case 'blocked':
        list = list.filter((r) => r.status === 'blocked');
        break;
      case 'review_needed':
        list = list.filter((r) => r.status === 'review_needed');
        break;
      case 'done':
        list = list.filter((r) => r.status === 'done' || r.status === 'not_applicable');
        break;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) => r.title.toLowerCase().includes(q) || r.item_id.toLowerCase().includes(q));
    }
    const sorted = [...list].sort((a, b) => {
      const dir = sortAsc ? 1 : -1;
      switch (sortKey) {
        case 'priority':
          return (a.priority_weight - b.priority_weight) * dir;
        case 'severity':
          return (SEVERITY_ORDINAL[a.severity] - SEVERITY_ORDINAL[b.severity]) * -dir;
        case 'title':
          return a.title.localeCompare(b.title) * dir;
        case 'category':
          return a.category.localeCompare(b.category) * dir;
        case 'status':
          return a.status.localeCompare(b.status) * dir;
      }
    });
    return sorted;
  }, [
    rows, search, severityFilter, categoryFilter, sourceFilter,
    chipFilter, assigneeByItemId, user?.id, sortKey, sortAsc,
  ]);

  const myOpenCount = useMemo(
    () =>
      rows.filter(
        (r) =>
          assigneeByItemId.get(r.item_id)?.user_id === user?.id &&
          (r.status === 'todo' || r.status === 'in_progress')
      ).length,
    [rows, assigneeByItemId, user?.id]
  );

  const categoriesInUse = useMemo(() => Array.from(new Set(rows.map((r) => r.category))).sort(), [rows]);

  async function changeStatus(itemId: string, status: CustomerRegisterItemStatus) {
    setSaving(itemId);
    setSaveError(null);
    try {
      await updateItemStatus(profileId, itemId, { status });
      assess.refetch();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSaving(null);
    }
  }

  const toggleSelect = useCallback((itemId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelected((prev) => {
      if (prev.size === filtered.length) return new Set();
      return new Set(filtered.map((r) => r.item_id));
    });
  }, [filtered]);

  async function bulkChangeStatus(
    status: CustomerRegisterItemStatus,
    opts?: { na_rationale?: string }
  ) {
    setSaveError(null);
    const ids = Array.from(selected);
    const previousStatuses = new Map<string, string>();
    for (const id of ids) {
      const row = rows.find((r) => r.item_id === id);
      if (row) previousStatuses.set(id, row.status);
    }
    for (const id of ids) {
      try {
        await updateItemStatus(profileId, id, { status, na_rationale: opts?.na_rationale });
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : `Failed to update ${id}`);
        break;
      }
    }
    setSelected(new Set());
    setShowBulkNa(false);
    assess.refetch();
    const timer = setTimeout(() => setUndoAction(null), 20000);
    setUndoAction({ ids, previousStatuses, timer });
  }

  async function undoBulk() {
    if (!undoRef.current) return;
    clearTimeout(undoRef.current.timer);
    const { ids, previousStatuses } = undoRef.current;
    setUndoAction(null);
    for (const id of ids) {
      const prev = previousStatuses.get(id) as CustomerRegisterItemStatus | undefined;
      if (prev) {
        try {
          await updateItemStatus(profileId, id, { status: prev });
        } catch {
          // best-effort
        }
      }
    }
    assess.refetch();
  }

  useEffect(() => {
    return () => {
      if (undoRef.current) clearTimeout(undoRef.current.timer);
    };
  }, []);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortAsc((x) => !x);
    else {
      setSortKey(key);
      setSortAsc(key === 'title' || key === 'category' || key === 'status');
    }
  }

  if (assess.loading) return <Spinner label="Loading register" />;
  if (assess.error) return <Alert variant="error">{assess.error}</Alert>;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PageHeader
        title="Register"
        subtitle={`${rows.length} applicable items · ${filtered.length} shown`}
        right={
          <Button type="button" onClick={() => navigate(`${prefix}/mine`)}>
            My work{myOpenCount > 0 ? ` (${myOpenCount})` : ''}
          </Button>
        }
      />

      {saveError ? <Alert variant="error">{saveError}</Alert> : null}

      {/* Glance strip — UC-3 R2 B4 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 'var(--space-3)',
        }}
      >
        <GlanceTile label="Open" value={glance.open} color="var(--text)" />
        <GlanceTile label="Blocked" value={glance.blocked} color="var(--critical)" />
        <GlanceTile label="Review needed" value={glance.reviewNeeded} color="var(--accent)" />
        <GlanceTile label="Closed" value={glance.closed} color="var(--success-fg)" />
      </div>

      {/* Blocked callout — UC-3 R2 H7 */}
      <BlockedCallout
        rows={rows}
        assigneeByItemId={assigneeByItemId}
        userId={user?.id ?? null}
        prefix={prefix}
      />

      {/* Filter chips */}
      <div
        role="radiogroup"
        aria-label="Quick filter"
        style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}
      >
        {FILTER_CHIPS.map((f) => {
          const active = chipFilter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setChipFilter(f.key)}
              style={{
                appearance: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '999px',
                border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                background: active ? 'var(--accent)' : 'var(--surface)',
                color: active ? 'var(--text-inverse)' : 'var(--text-muted)',
                fontSize: 'var(--font-size-3xs)',
                fontWeight: active ? 700 : 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {f.label}
              <span style={{ fontFamily: 'var(--font-mono)', opacity: active ? 1 : 0.7 }}>
                {chipCounts[f.key]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Dropdowns */}
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          padding: 'var(--space-4)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-3)',
          alignItems: 'flex-end',
        }}
      >
        <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Field id="reg-search" label="Search">
            <Input
              placeholder="Title or item ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Field>
        </div>
        <Field id="reg-sev" label="Severity">
          <Select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value as Severity | 'all')}>
            <option value="all">All</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="elevated">Elevated</option>
            <option value="moderate">Moderate</option>
            <option value="standard">Standard</option>
          </Select>
        </Field>
        <Field id="reg-cat" label="Category">
          <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value as RiskCategory | 'all')}>
            <option value="all">All</option>
            {categoriesInUse.map((c) => (
              <option key={c} value={c}>
                {categoryLabel[c]}
              </option>
            ))}
          </Select>
        </Field>
        <Field id="reg-source" label="Source">
          <Select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value as SourceType | 'all')}>
            <option value="all">All</option>
            <option value="framework">Framework</option>
            <option value="legislation">Legislation</option>
            <option value="case_law">Case law</option>
            <option value="breach_intelligence">Breach intel</option>
            <option value="best_practice">Best practice</option>
            <option value="expert_analysis">Expert analysis</option>
          </Select>
        </Field>
      </div>

      {/* Register table */}
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          overflow: 'hidden',
        }}
      >
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-2xs)',
              color: 'var(--text)',
              borderCollapse: 'collapse',
            }}
          >
            <thead style={{ background: 'var(--surface-elevated)', color: 'var(--text-dim)' }}>
              <tr>
                <th style={{ padding: 'var(--space-2)', width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={selected.size > 0 && selected.size === filtered.length}
                    onChange={toggleSelectAll}
                    aria-label="Select all"
                  />
                </th>
                <Th active={sortKey === 'severity'} asc={sortAsc} onClick={() => toggleSort('severity')}>
                  Severity
                </Th>
                <Th active={sortKey === 'title'} asc={sortAsc} onClick={() => toggleSort('title')}>
                  Item
                </Th>
                <Th>Assignee</Th>
                <Th>Frameworks</Th>
                <Th active={sortKey === 'status'} asc={sortAsc} onClick={() => toggleSort('status')}>
                  Status
                </Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const isSelected = selected.has(r.item_id);
                return (
                  <tr
                    key={r.item_id}
                    style={{
                      borderTop: '1px solid var(--border)',
                      background: isSelected ? 'var(--surface-elevated)' : undefined,
                    }}
                  >
                    <td style={{ padding: 'var(--space-2)', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(r.item_id)}
                        aria-label={`Select ${r.title}`}
                      />
                    </td>
                    <td style={{ padding: 'var(--space-2) var(--space-3)' }}>
                      <SeverityBadge severity={r.severity} />
                    </td>
                    <td style={{ padding: 'var(--space-2) var(--space-3)' }}>
                      <Link
                        to={`${prefix}/register/${r.item_id}`}
                        style={{ fontWeight: 500, color: 'var(--text)', textDecoration: 'underline', textDecorationColor: 'var(--border-strong)' }}
                        title={r.item_id}
                      >
                        {r.title}
                      </Link>
                    </td>
                    <td style={{ padding: 'var(--space-2) var(--space-3)' }}>
                      <Link
                        to={`${prefix}/register/${r.item_id}`}
                        style={{
                          color: assigneeByItemId.get(r.item_id) ? 'var(--text-muted)' : 'var(--text-dim)',
                          textDecoration: 'underline',
                          textDecorationColor: 'var(--border-strong)',
                        }}
                        title="Open item to reassign"
                      >
                        {assigneeByItemId.get(r.item_id)?.display_name ?? 'Unassigned'}
                      </Link>
                    </td>
                    <td style={{ padding: 'var(--space-2) var(--space-3)', color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
                      {r.applicable_frameworks.slice(0, 3).map(id => frameworkNameMap.get(id) ?? id).join(', ')}
                      {r.applicable_frameworks.length > 3 ? ` +${r.applicable_frameworks.length - 3}` : ''}
                    </td>
                    <td style={{ padding: 'var(--space-2) var(--space-3)' }}>
                      {r.status === 'review_needed' ? (
                        <ReviewCluster
                          itemId={r.item_id}
                          saving={saving === r.item_id}
                          onApprove={() => changeStatus(r.item_id, 'done')}
                          onReject={() => changeStatus(r.item_id, 'in_progress')}
                        />
                      ) : (
                        <StatusBadge status={r.status} />
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: 'var(--space-6)', textAlign: 'center', color: 'var(--text-dim)' }}>
                    No items match the current filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bulk-actions bar — UC-3 R2 (owner/admin/editor only) */}
      {selected.size > 0 && !isAssessor ? (
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 60,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-3) var(--space-4)',
            background: 'var(--surface-elevated)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-card)',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 600, color: 'var(--text)' }}>
            {selected.size} selected
          </span>
          <Button type="button" onClick={() => bulkChangeStatus('done')}>
            Mark done
          </Button>
          <Button type="button" onClick={() => setShowBulkNa(true)}>
            Mark N/A
          </Button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            style={{
              marginLeft: 'auto',
              appearance: 'none',
              background: 'none',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-3xs)',
              textDecoration: 'underline',
            }}
          >
            Clear selection
          </button>
        </div>
      ) : null}

      {/* Bulk N/A dialog — UC-3 R2 B2 */}
      <BulkNaDialog
        open={showBulkNa}
        selectedCount={selected.size}
        onConfirm={(rationale) => bulkChangeStatus('not_applicable', { na_rationale: rationale })}
        onCancel={() => setShowBulkNa(false)}
      />

      {/* 20s undo snackbar — UC-3 R2 B10 */}
      {undoAction ? (
        <div
          style={{
            position: 'fixed',
            bottom: 'var(--space-4)',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 70,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-3) var(--space-4)',
            background: 'var(--surface-elevated)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-card)',
            boxShadow: 'var(--shadow-md)',
            fontSize: 'var(--font-size-2xs)',
            color: 'var(--text)',
          }}
          role="status"
          aria-live="polite"
          onMouseEnter={() => {
            if (undoRef.current) clearTimeout(undoRef.current.timer);
          }}
          onMouseLeave={() => {
            if (undoRef.current) {
              const t = setTimeout(() => setUndoAction(null), 20000);
              setUndoAction({ ...undoRef.current, timer: t });
            }
          }}
        >
          <span>{undoAction.ids.length} item{undoAction.ids.length !== 1 ? 's' : ''} updated</span>
          <button
            type="button"
            onClick={undoBulk}
            style={{
              appearance: 'none',
              padding: '4px 12px',
              border: '1px solid var(--accent)',
              borderRadius: 'var(--radius-input)',
              background: 'transparent',
              color: 'var(--accent)',
              fontSize: 'var(--font-size-3xs)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Undo
          </button>
        </div>
      ) : null}

      {rows.length === 0 ? (
        <EmptyState
          title="No items applicable yet"
          description={
            <>
              If this looks wrong, revisit your profile —{' '}
              <Link to="/onboarding" style={{ color: 'var(--secondary)' }}>edit onboarding</Link> or contact support.
            </>
          }
        />
      ) : (
        <div
          style={{
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text-dim)',
            fontFamily: 'var(--font-ui)',
          }}
        >
          Showing {filtered.length} of {rows.length} items
        </div>
      )}
    </div>
  );
}

// ---- Glance tile --------------------------------------------------------

function GlanceTile({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4)',
      }}
    >
      <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color, fontFamily: 'var(--font-mono)' }}>
        {value}
      </div>
    </div>
  );
}

// ---- Blocked callout (owed-by-you / owed-to-you split) ------------------

function BlockedCallout({
  rows,
  assigneeByItemId,
  userId,
  prefix,
}: {
  rows: GapAnalysisRow[];
  assigneeByItemId: Map<string, { user_id: string; email: string; display_name: string } | null>;
  userId: string | null;
  prefix: string;
}) {
  const blocked = rows.filter((r) => r.status === 'blocked');
  if (blocked.length === 0) return null;

  const owedByYou = blocked.filter((r) => {
    const assignee = assigneeByItemId.get(r.item_id);
    return assignee && assignee.user_id !== userId;
  });
  const owedToYou = blocked.filter((r) => {
    const assignee = assigneeByItemId.get(r.item_id);
    return assignee?.user_id === userId;
  });

  return (
    <section
      aria-label="Blocked items"
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <div style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 700, color: 'var(--critical)' }}>
        {blocked.length} item{blocked.length !== 1 ? 's' : ''} blocked
      </div>
      {owedByYou.length > 0 ? (
        <div>
          <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--critical)', fontWeight: 600, marginBottom: 'var(--space-2)' }}>
            Owed by you ({owedByYou.length})
          </div>
          {owedByYou.map((r) => (
            <div key={r.item_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-2) 0', borderTop: '1px solid var(--border)' }}>
              <Link to={`${prefix}/register/${r.item_id}`} style={{ color: 'var(--text)', fontWeight: 500, fontSize: 'var(--font-size-2xs)' }}>
                {r.title}
              </Link>
              <Link to={`${prefix}/register/${r.item_id}`} style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--accent)', fontWeight: 600 }}>
                Reply →
              </Link>
            </div>
          ))}
        </div>
      ) : null}
      {owedToYou.length > 0 ? (
        <div>
          <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--warning-fg)', fontWeight: 600, marginBottom: 'var(--space-2)' }}>
            Owed to you ({owedToYou.length})
          </div>
          {owedToYou.map((r) => (
            <div key={r.item_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-2) 0', borderTop: '1px solid var(--border)' }}>
              <Link to={`${prefix}/register/${r.item_id}`} style={{ color: 'var(--text)', fontWeight: 500, fontSize: 'var(--font-size-2xs)' }}>
                {r.title}
              </Link>
              <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
                Waiting on {assigneeByItemId.get(r.item_id)?.display_name ?? 'someone'}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

// ---- Review cluster (Approve / Request changes) -------------------------

function ReviewCluster({
  itemId,
  saving,
  onApprove,
  onReject,
}: {
  itemId: string;
  saving: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <div style={{ display: 'flex', gap: 'var(--space-1)', position: 'relative' }}>
      {confirming ? (
        <div style={{ display: 'flex', gap: 'var(--space-1)', alignItems: 'center', fontSize: 'var(--font-size-3xs)' }}>
          <span style={{ color: 'var(--text-muted)' }}>Mark done?</span>
          <button type="button" onClick={() => { onApprove(); setConfirming(false); }} disabled={saving} style={{ appearance: 'none', padding: '2px 8px', borderRadius: 'var(--radius-input)', border: '1px solid var(--band-green)', background: 'var(--band-green)', color: 'var(--text-inverse)', fontSize: 'var(--font-size-3xs)', fontWeight: 600, cursor: 'pointer' }}>Yes</button>
          <button type="button" onClick={() => setConfirming(false)} style={{ appearance: 'none', padding: '2px 8px', borderRadius: 'var(--radius-input)', border: '1px solid var(--border-strong)', background: 'var(--surface-alt)', color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)', cursor: 'pointer' }}>No</button>
        </div>
      ) : (
      <>
        <button
          type="button"
          disabled={saving}
          onClick={() => setConfirming(true)}
          aria-label={`Approve ${itemId}`}
          style={{
            appearance: 'none',
            padding: '6px 12px',
            borderRadius: 'var(--radius-input)',
            border: '1px solid var(--band-green)',
            background: 'var(--band-green)',
            color: 'var(--text-inverse)',
            fontSize: 'var(--font-size-3xs)',
            fontWeight: 600,
            cursor: 'pointer',
            minHeight: '32px',
          }}
        >
          Approve
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={onReject}
          aria-label={`Request changes on ${itemId}`}
          style={{
            appearance: 'none',
            padding: '6px 12px',
            borderRadius: 'var(--radius-input)',
            border: '1px solid var(--border-strong)',
            background: 'var(--surface-alt)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-3xs)',
            cursor: 'pointer',
            minHeight: '32px',
          }}
        >
          Changes
        </button>
      </>
      )}
    </div>
  );
}

// ---- Sortable column header ---------------------------------------------

function Th({
  children,
  active,
  asc,
  onClick,
}: {
  children: React.ReactNode;
  active?: boolean;
  asc?: boolean;
  onClick?: () => void;
}) {
  const style = {
    textAlign: 'left' as const,
    padding: 'var(--space-2) var(--space-3)',
    fontWeight: 500,
    fontSize: 'var(--font-size-3xs)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
  };
  return (
    <th style={style}>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          style={{
            appearance: 'none',
            background: 'none',
            border: 'none',
            padding: 0,
            margin: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            cursor: 'pointer',
            font: 'inherit',
            color: 'inherit',
            textTransform: 'inherit',
            letterSpacing: 'inherit',
          }}
        >
          {children}
          {active ? <span style={{ color: 'var(--accent)' }}>{asc ? '↑' : '↓'}</span> : null}
        </button>
      ) : (
        children
      )}
    </th>
  );
}
